import { api } from '@/api'
import Button from '@/components/Button'
import HtmlContent from '@/components/HtmlContent'
import { PlaceholderLoading } from '@/components/PlaceholderLoading'
import Section from '@/components/Section'
import Title from '@/components/Title'
import { STAGE_TYPE } from '@/const'
import { useCurrentUser, useModal, usePage } from '@/hooks'
import { arrowRightIcon, gameIcon, medalIcon, trophyIcon } from '@/images'
import { dayjs } from '@/utils'
import { GxIcon } from '@garpix/garpix-web-components-react'
import classNames from 'classnames'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import style from '../styles/tourn_detail.module.scss'

interface IProps {
  id: string | number
  title: string
  gameDetailUrl: string | undefined
  description: string
  date: string | null
  isFinishedValue: string
  registerClosedValue: string
  stage: string
  gameId: string | number
  currentUserRegistered: boolean
  image: string | null
}

const TournamentInfo = ({
  id,
  gameId,
  title,
  gameDetailUrl,
  description,
  date,
  isFinishedValue,
  registerClosedValue,
  stage,
  currentUserRegistered,
  image
}: IProps): React.ReactElement => {
  const [closing, setClosing] = useState(false)
  const navigate = useNavigate()
  const { openModal } = useModal()
  const { isLogin, currentUser } = useCurrentUser()
  const page = usePage()

  const { config } = page.init_state.global

  const isFinish = Boolean(Number(isFinishedValue))
  const isWaiting = Boolean(Number(registerClosedValue)) && !isFinish
  const isOpen = !isWaiting && !isFinish

  const isAdmin = currentUser?.is_admin

  const handleOpenModal = (): void => {
    if (isLogin) {
      openModal('enterTournament', {
        tournamentId: id,
        gameId
      })
    } else {
      navigate(config.authorization)
    }
  }

  const handleClose = (): void => {
    if (id !== undefined) {
      setClosing(true)
      api
        .closeChampionship({
          id: id
        })
        .then(() => window.location.reload())
        .catch(() => setClosing(false))
    }
  }

  return (
    <Section>
      <div className={style.detail_head}>
        <div
          className={classNames({
            [style.status]: true,
            [style.status__open]: isOpen,
            [style.status__completed]: isFinish,
            [style.status__waiting]: isWaiting
          })}
        >
          <GxIcon src={medalIcon} className={style.status__icon} />
          <span className={style.status__text}>
            {isOpen ? 'Открыт' : ''}
            {isWaiting ? 'Ожидание завершения' : ''}
            {isFinish ? 'Завершен' : ''}
          </span>

          <span className={style.status__text}>{STAGE_TYPE[stage]}</span>

          <span
            className={classNames({
              [style.status__date]: true,
              [style.status__date_open]: isOpen,
              [style.status__date_waiting]: isWaiting
            })}
          >
            {date !== ''
              ? dayjs(date, 'DD.MM.YYYY HH:mm:SS').format('DD.MM.YYYY')
              : null}
          </span>
        </div>
        <div className={style.detail_info}>
          {image !== null
            ? (
              <img
                src={image}
                width='270px'
                height='270px'
                alt='Игра'
                className={style.detail__screenshot}
              />
              )
            : (
              <div
                className={classNames({
                  [style.detail_empty]: true,
                  [style.detail_empty__open]: isOpen,
                  [style.detail_empty__completed]: isFinish,
                  [style.detail_empty__waiting]: isWaiting
                })}
              >
                <GxIcon src={gameIcon} className={style.detail_empty__icon} />
              </div>
              )}

          <div className={style.detail_info_right}>
            <Title type='h2' className={style.detail__title}>
              {title}
            </Title>
            {gameDetailUrl !== undefined
              ? (
                <Link to={gameDetailUrl} className={style.detail__link}>
                  К игре
                  <GxIcon
                    src={arrowRightIcon}
                    className={style.detail__link_icon}
                  />
                </Link>
                )
              : (
                <PlaceholderLoading shape='rect' width={96} height={24} />
                )}
          </div>
        </div>

        {isOpen && !currentUserRegistered && !isAdmin
          ? (
            <Button
              size='large'
              highlight
              wide
              corner
              onClick={handleOpenModal}
              className={style.detail__participate}
            >
              Участвовать
            </Button>
            )
          : null}

        {isAdmin && !isFinish
          ? (
            <Button
              size='large'
              highlight
              wide
              corner
              className={style.detail__participate}
              onClick={handleClose}
              disabled={closing}
            >
              <GxIcon src={trophyIcon} slot='icon-left' />
              Завершить этап
            </Button>
            )
          : null}
      </div>
      {description !== null && description.length > 0
        ? (
          <div className={style.detail_desc}>
            <Title type='h2' theme='h3' className={style.detail_desc__title}>
              Описание
            </Title>
            <HtmlContent
              className={style.detail_desc__text}
              content={description}
            />
          </div>
          )
        : null}
    </Section>
  )
}

export default TournamentInfo
